/**
 * EventPady+ Route Registry
 */
import { createBrowserRouter, Navigate } from 'react-router-dom'
import { lazy, Suspense, type ReactNode } from 'react'
import { AuthLayout } from '@/layouts/AuthLayout'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { ProtectedRoute } from './ProtectedRoute'
import { PublicRoute } from './PublicRoute'
import { ErrorBoundary } from '@/components/shared/ErrorBoundary'
import { LoadingSpinner } from '@/components/shared/LoadingSpinner'
import { ROUTES } from '@/constants/routes'
import { useAuth } from '@/hooks/useAuth'

/**
 * Root redirect: SUPER_ADMIN → platform dashboard, everyone else → org dashboard.
 *
 * IMPORTANT: We MUST wait for isLoading=false before redirecting. If we redirect
 * while auth is still resolving, role is null and we'd send SUPER_ADMIN to
 * /dashboard — which makes org-scoped API calls that 401, triggering a failed
 * token refresh, which logs the user out and causes the blank white screen.
 */
function HomeRedirect() {
  const { role, isLoading } = useAuth()

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  let dest: string
  if (role === 'SUPER_ADMIN') {
    dest = ROUTES.PLATFORM_ADMIN.DASHBOARD
  } else if (role === 'CAPTURE_AGENT') {
    dest = ROUTES.CAPTURE
  } else {
    dest = ROUTES.DASHBOARD
  }
  return <Navigate to={dest} replace />
}

function lazyPage(factory: () => Promise<{ default: React.ComponentType }>): ReactNode {
  const LazyComponent = lazy(factory)
  return (
    <Suspense
      fallback={
        <div className="flex min-h-[400px] items-center justify-center">
          <LoadingSpinner size="lg" />
        </div>
      }
    >
      <LazyComponent />
    </Suspense>
  )
}

// Pre-Registrations (V1.3)
const PreRegistrationsPage = () => lazyPage(() => import('@/pages/pre-registrations/PreRegistrationsPage'))
const PreRegisterPage      = () => lazyPage(() => import('@/pages/pre-registrations/PreRegisterPage'))

// Auth
const LoginPage               = () => lazyPage(() => import('@/pages/auth/LoginPage'))
const ForgotPasswordPage      = () => lazyPage(() => import('@/pages/auth/ForgotPasswordPage'))
const ResetPasswordPage       = () => lazyPage(() => import('@/pages/auth/ResetPasswordPage'))
const AcceptInvitationPage    = () => lazyPage(() => import('@/pages/auth/AcceptInvitationPage'))
const SignupPage              = () => lazyPage(() => import('@/pages/auth/SignupPage'))

// Onboarding (public — no auth, own layout)
const PaymentProcessingPage  = () => lazyPage(() => import('@/pages/onboarding/PaymentProcessingPage'))
const PendingApprovalPage    = () => lazyPage(() => import('@/pages/onboarding/PendingApprovalPage'))

// Dashboard
const DashboardPage = () => lazyPage(() => import('@/pages/dashboard/DashboardPage'))

// Visitors
const VisitorsPage      = () => lazyPage(() => import('@/pages/visitors/VisitorsPage'))
const VisitorDetailPage = () => lazyPage(() => import('@/pages/visitors/VisitorDetailPage'))

// SMS
const SMSPage               = () => lazyPage(() => import('@/pages/sms/SMSPage'))
const SMSTemplatesPage      = () => lazyPage(() => import('@/pages/sms/SMSTemplatesPage'))
const SMSCampaignsPage      = () => lazyPage(() => import('@/pages/sms/SMSCampaignsPage'))
const SMSCampaignDetailPage = () => lazyPage(() => import('@/pages/sms/SMSCampaignDetailPage'))
const SMSCreditsPage        = () => lazyPage(() => import('@/pages/sms/SMSCreditsPage'))

// Compliance
const CompliancePage      = () => lazyPage(() => import('@/pages/compliance/CompliancePage'))
const ComplianceRulesPage = () => lazyPage(() => import('@/pages/compliance/ComplianceRulesPage'))

// Analytics
const AnalyticsPage = () => lazyPage(() => import('@/pages/analytics/AnalyticsPage'))
const ExportsPage   = () => lazyPage(() => import('@/pages/analytics/ExportsPage'))

// Audit
const AuditLogsPage = () => lazyPage(() => import('@/pages/audit/AuditLogsPage'))

// Search
const SearchResultsPage = () => lazyPage(() => import('@/pages/search/SearchResultsPage'))

// Settings
const SettingsPage           = () => lazyPage(() => import('@/pages/settings/SettingsPage'))
const AccountSettingsPage    = () => lazyPage(() => import('@/pages/settings/AccountSettingsPage'))
const ProfileSettingsPage    = () => lazyPage(() => import('@/pages/settings/ProfileSettingsPage'))
const OrgSettingsPage        = () => lazyPage(() => import('@/pages/settings/OrgSettingsPage'))
const MembersPage            = () => lazyPage(() => import('@/pages/settings/MembersPage'))
const SubscriptionPage       = () => lazyPage(() => import('@/pages/settings/SubscriptionPage'))
const BillingPage            = () => lazyPage(() => import('@/pages/settings/BillingPage'))
const PaymentPage            = () => lazyPage(() => import('@/pages/settings/PaymentPage'))
const VisitorPurposesPage    = () => lazyPage(() => import('@/pages/settings/VisitorPurposesPage'))
const WorkflowConfigPage     = () => lazyPage(() => import('@/pages/settings/WorkflowConfigPage'))
const WorkflowDetailPage     = () => lazyPage(() => import('@/pages/settings/WorkflowDetailPage'))
const KioskManagementPage    = () => lazyPage(() => import('@/pages/settings/KioskManagementPage'))
const FieldConfigPage        = () => lazyPage(() => import('@/pages/settings/FieldConfigPage'))

// Capture Agent (mobile-only — CAPTURE_AGENT role)
const CaptureAgentPage = () => lazyPage(() => import('@/pages/capture/CaptureAgentPage'))

// Kiosk (public — no auth, full-screen tablet UI)
const KioskPage = () => lazyPage(() => import('@/pages/kiosk/KioskPage'))

// Platform Admin (SUPER_ADMIN only)
const PlatformDashboardPage       = () => lazyPage(() => import('@/pages/platform-admin/PlatformDashboardPage'))
const PlatformOrganizationsPage   = () => lazyPage(() => import('@/pages/platform-admin/PlatformOrganizationsPage'))
const PlatformOrgDetailPage       = () => lazyPage(() => import('@/pages/platform-admin/PlatformOrgDetailPage'))
const PlatformPlansPage           = () => lazyPage(() => import('@/pages/platform-admin/PlatformPlansPage'))
const PlatformRequestsPage        = () => lazyPage(() => import('@/pages/platform-admin/PlatformRequestsPage'))
const PlatformSMSTemplatesPage    = () => lazyPage(() => import('@/pages/platform-admin/PlatformSMSTemplatesPage'))
const PlatformSMSMessagesPage     = () => lazyPage(() => import('@/pages/platform-admin/PlatformSMSMessagesPage'))
const PlatformSMSCampaignsPage    = () => lazyPage(() => import('@/pages/platform-admin/PlatformSMSCampaignsPage'))
const PlatformSMSCreditsPage      = () => lazyPage(() => import('@/pages/platform-admin/PlatformSMSCreditsPage'))
const PlatformSMSTransactionsPage    = () => lazyPage(() => import('@/pages/platform-admin/PlatformSMSTransactionsPage'))
const PlatformUsersPage               = () => lazyPage(() => import('@/pages/platform-admin/PlatformUsersPage'))
const PlatformMembershipsPage         = () => lazyPage(() => import('@/pages/platform-admin/PlatformMembershipsPage'))
const PlatformPasswordResetTokensPage = () => lazyPage(() => import('@/pages/platform-admin/PlatformPasswordResetTokensPage'))
const PlatformRefreshTokensPage       = () => lazyPage(() => import('@/pages/platform-admin/PlatformRefreshTokensPage'))
const PlatformKioskSessionsPage       = () => lazyPage(() => import('@/pages/platform-admin/PlatformKioskSessionsPage'))
const PlatformInvitationsPage         = () => lazyPage(() => import('@/pages/platform-admin/PlatformInvitationsPage'))
const PlatformOrgSettingsPage         = () => lazyPage(() => import('@/pages/platform-admin/PlatformOrgSettingsPage'))
const PlatformOrgPaymentsPage         = () => lazyPage(() => import('@/pages/platform-admin/PlatformOrgPaymentsPage'))
const PlatformSubscriptionsPage       = () => lazyPage(() => import('@/pages/platform-admin/PlatformSubscriptionsPage'))
const PlatformVisitorPurposesAdminPage = () => lazyPage(() => import('@/pages/platform-admin/PlatformVisitorPurposesAdminPage'))
const PlatformVisitLogsPage           = () => lazyPage(() => import('@/pages/platform-admin/PlatformVisitLogsPage'))
const PlatformVisitorsAdminPage       = () => lazyPage(() => import('@/pages/platform-admin/PlatformVisitorsAdminPage'))

// 404
const NotFoundPage = () => lazyPage(() => import('@/pages/NotFoundPage'))

export const router = createBrowserRouter([
  {
    element: (
      <PublicRoute>
        <AuthLayout />
      </PublicRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      { path: ROUTES.AUTH.LOGIN,                             element: <LoginPage /> },
      { path: ROUTES.AUTH.FORGOT_PASSWORD,                   element: <ForgotPasswordPage /> },
      { path: `${ROUTES.AUTH.RESET_PASSWORD}/:token`,        element: <ResetPasswordPage /> },
      { path: `${ROUTES.AUTH.ACCEPT_INVITATION}/:token`,     element: <AcceptInvitationPage /> },
    ],
  },
  {
    element: (
      <ProtectedRoute>
        <DashboardLayout />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
    children: [
      { path: '/', element: <HomeRedirect /> },
      { path: ROUTES.DASHBOARD, element: <DashboardPage /> },

      // Visitors
      { path: ROUTES.VISITORS.LIST, element: <VisitorsPage /> },
      { path: '/visitors/:id',      element: <VisitorDetailPage /> },

      // Pre-Registrations (V1.3) — staff routes inside DashboardLayout
      { path: ROUTES.PRE_REGISTRATIONS.LIST, element: <PreRegistrationsPage /> },

      // SMS
      { path: ROUTES.SMS.LIST,      element: <SMSPage /> },
      { path: ROUTES.SMS.TEMPLATES, element: <SMSTemplatesPage /> },
      { path: ROUTES.SMS.CAMPAIGNS, element: <SMSCampaignsPage /> },
      { path: '/sms/campaigns/:id', element: <SMSCampaignDetailPage /> },
      { path: ROUTES.SMS.CREDITS,   element: <SMSCreditsPage /> },

      // Compliance
      { path: ROUTES.COMPLIANCE.OVERVIEW, element: <CompliancePage /> },
      {
        path: ROUTES.COMPLIANCE.RULES,
        element: (
          <ProtectedRoute requiredRole="ORG_OWNER">
            <ComplianceRulesPage />
          </ProtectedRoute>
        ),
      },

      // Global search
      { path: ROUTES.SEARCH, element: <SearchResultsPage /> },

      // Analytics & Audit
      { path: ROUTES.ANALYTICS.OVERVIEW, element: <AnalyticsPage /> },
      { path: ROUTES.ANALYTICS.EXPORTS,  element: <ExportsPage /> },
      { path: ROUTES.AUDIT_LOGS, element: <AuditLogsPage /> },

      // Settings
      { path: ROUTES.SETTINGS.ROOT,    element: <SettingsPage /> },
      { path: ROUTES.SETTINGS.ACCOUNT, element: <AccountSettingsPage /> },
      { path: ROUTES.SETTINGS.PROFILE, element: <ProfileSettingsPage /> },
      {
        path: ROUTES.SETTINGS.ORGANIZATION,
        element: (
          <ProtectedRoute requiredRole="ORG_OWNER">
            <OrgSettingsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.SETTINGS.MEMBERS,
        element: (
          <ProtectedRoute requiredRole="ORG_ADMIN">
            <MembersPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.SETTINGS.SUBSCRIPTION,
        element: (
          <ProtectedRoute requiredRole="ORG_OWNER">
            <SubscriptionPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.SETTINGS.BILLING,
        element: (
          <ProtectedRoute requiredRole="ORG_OWNER">
            <BillingPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/settings/billing/pay/:type/:id',
        element: (
          <ProtectedRoute requiredRole="ORG_OWNER">
            <PaymentPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.SETTINGS.VISITOR_PURPOSES,
        element: (
          <ProtectedRoute requiredRole="ORG_ADMIN">
            <VisitorPurposesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.SETTINGS.WORKFLOWS,
        element: (
          <ProtectedRoute requiredRole="ORG_ADMIN">
            <WorkflowConfigPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/settings/workflows/:purposeId',
        element: (
          <ProtectedRoute requiredRole="ORG_ADMIN">
            <WorkflowDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.SETTINGS.KIOSK,
        element: (
          <ProtectedRoute requiredRole="ORG_ADMIN">
            <KioskManagementPage />
          </ProtectedRoute>
        ),
      },
      {
        // ORG_ADMIN can view; ORG_OWNER can edit — enforcement inside the page
        path: ROUTES.SETTINGS.FIELD_CONFIG,
        element: (
          <ProtectedRoute requiredRole="ORG_ADMIN">
            <FieldConfigPage />
          </ProtectedRoute>
        ),
      },

      // Platform Admin -- SUPER_ADMIN only
      {
        path: ROUTES.PLATFORM_ADMIN.DASHBOARD,
        element: (
          <ProtectedRoute requiredRole="SUPER_ADMIN">
            <PlatformDashboardPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.PLATFORM_ADMIN.ORGANIZATIONS,
        element: (
          <ProtectedRoute requiredRole="SUPER_ADMIN">
            <PlatformOrganizationsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: '/platform/organizations/:id',
        element: (
          <ProtectedRoute requiredRole="SUPER_ADMIN">
            <PlatformOrgDetailPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.PLATFORM_ADMIN.PLANS,
        element: (
          <ProtectedRoute requiredRole="SUPER_ADMIN">
            <PlatformPlansPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.PLATFORM_ADMIN.REQUESTS,
        element: (
          <ProtectedRoute requiredRole="SUPER_ADMIN">
            <PlatformRequestsPage />
          </ProtectedRoute>
        ),
      },

      // Platform SMS Management
      {
        path: ROUTES.PLATFORM_ADMIN.SMS_TEMPLATES,
        element: (
          <ProtectedRoute requiredRole="SUPER_ADMIN">
            <PlatformSMSTemplatesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.PLATFORM_ADMIN.SMS_MESSAGES,
        element: (
          <ProtectedRoute requiredRole="SUPER_ADMIN">
            <PlatformSMSMessagesPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.PLATFORM_ADMIN.SMS_CAMPAIGNS,
        element: (
          <ProtectedRoute requiredRole="SUPER_ADMIN">
            <PlatformSMSCampaignsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.PLATFORM_ADMIN.SMS_CREDITS,
        element: (
          <ProtectedRoute requiredRole="SUPER_ADMIN">
            <PlatformSMSCreditsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.PLATFORM_ADMIN.SMS_TRANSACTIONS,
        element: (
          <ProtectedRoute requiredRole="SUPER_ADMIN">
            <PlatformSMSTransactionsPage />
          </ProtectedRoute>
        ),
      },

      // Platform Auth Admin
      {
        path: ROUTES.PLATFORM_ADMIN.USERS,
        element: (
          <ProtectedRoute requiredRole="SUPER_ADMIN">
            <PlatformUsersPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.PLATFORM_ADMIN.MEMBERSHIPS,
        element: (
          <ProtectedRoute requiredRole="SUPER_ADMIN">
            <PlatformMembershipsPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.PLATFORM_ADMIN.PASSWORD_RESET_TOKENS,
        element: (
          <ProtectedRoute requiredRole="SUPER_ADMIN">
            <PlatformPasswordResetTokensPage />
          </ProtectedRoute>
        ),
      },
      {
        path: ROUTES.PLATFORM_ADMIN.REFRESH_TOKENS,
        element: (
          <ProtectedRoute requiredRole="SUPER_ADMIN">
            <PlatformRefreshTokensPage />
          </ProtectedRoute>
        ),
      },

      // Platform Kiosk Admin
      {
        path: ROUTES.PLATFORM_ADMIN.KIOSK_SESSIONS,
        element: (
          <ProtectedRoute requiredRole="SUPER_ADMIN">
            <PlatformKioskSessionsPage />
          </ProtectedRoute>
        ),
      },

      // Platform Org Invitations
      {
        path: ROUTES.PLATFORM_ADMIN.INVITATIONS,
        element: (
          <ProtectedRoute requiredRole="SUPER_ADMIN">
            <PlatformInvitationsPage />
          </ProtectedRoute>
        ),
      },

      // Platform Org Settings
      {
        path: ROUTES.PLATFORM_ADMIN.ORG_SETTINGS,
        element: (
          <ProtectedRoute requiredRole="SUPER_ADMIN">
            <PlatformOrgSettingsPage />
          </ProtectedRoute>
        ),
      },

      // Platform Org Payments
      {
        path: ROUTES.PLATFORM_ADMIN.ORG_PAYMENTS,
        element: (
          <ProtectedRoute requiredRole="SUPER_ADMIN">
            <PlatformOrgPaymentsPage />
          </ProtectedRoute>
        ),
      },

      // Platform Subscriptions
      {
        path: ROUTES.PLATFORM_ADMIN.SUBSCRIPTIONS,
        element: (
          <ProtectedRoute requiredRole="SUPER_ADMIN">
            <PlatformSubscriptionsPage />
          </ProtectedRoute>
        ),
      },

      // Platform Visitor Purposes (cross-tenant)
      {
        path: ROUTES.PLATFORM_ADMIN.VISITOR_PURPOSES_ADMIN,
        element: (
          <ProtectedRoute requiredRole="SUPER_ADMIN">
            <PlatformVisitorPurposesAdminPage />
          </ProtectedRoute>
        ),
      },

      // Platform Visit Logs
      {
        path: ROUTES.PLATFORM_ADMIN.VISIT_LOGS,
        element: (
          <ProtectedRoute requiredRole="SUPER_ADMIN">
            <PlatformVisitLogsPage />
          </ProtectedRoute>
        ),
      },

      // Platform Visitors (cross-tenant)
      {
        path: ROUTES.PLATFORM_ADMIN.VISITORS_ADMIN,
        element: (
          <ProtectedRoute requiredRole="SUPER_ADMIN">
            <PlatformVisitorsAdminPage />
          </ProtectedRoute>
        ),
      },
    ],
  },
  // Capture Agent — full-screen mobile capture interface, no sidebar
  {
    path: ROUTES.CAPTURE,
    element: (
      <ProtectedRoute requiredRole="CAPTURE_AGENT">
        <CaptureAgentPage />
      </ProtectedRoute>
    ),
    errorElement: <ErrorBoundary />,
  },

  // Public pre-register page — no auth, no sidebar, minimal shell
  {
    path: '/pre-register/:token',
    element: <PreRegisterPage />,
    errorElement: <ErrorBoundary />,
  },

  // Kiosk — public full-screen tablet UI, no auth required
  {
    path: '/kiosk/:token',
    element: <KioskPage />,
    errorElement: <ErrorBoundary />,
  },

  // Onboarding — public, own full-page layout
  {
    path: ROUTES.AUTH.REGISTER,               // '/signup'
    element: <SignupPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: '/payment/processing/:reference',
    element: <PaymentProcessingPage />,
    errorElement: <ErrorBoundary />,
  },
  {
    path: ROUTES.PENDING_APPROVAL,            // '/pending-approval'
    element: <PendingApprovalPage />,
    errorElement: <ErrorBoundary />,
  },

  { path: '*', element: <NotFoundPage /> },
])
